import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import Cal, {getCalApi} from '@calcom/embed-react'
import {Box} from '@mui/material'
import {
  getProductLineColors,
  useProductLine,
} from '@progynyglobal/pg-ui-library'

import AlertSnackBar from '@progyny/components/AlertSnackBar/AlertSnackBar'
import CalendarLoader from '@progyny/components/CalendarLoader'
import {useLanguageCriteria} from '@progyny/store/languageStore'
import type {BookingField} from '@progyny/types/calcom'
import {
  REACT_APP_CALCOM_EMBED_URL,
  REACT_APP_CALCOM_ORIGIN,
} from '@progyny/utils/env'

interface BookingSuccessfulData {
  confirmed: boolean
  eventType: object
  date: string
  duration: number
  organizer: object
}

interface CalendarEmbedProps {
  calLink: string
  title?: string
  description?: string
  height?: string | number
  onBookingSuccessful?: (data: BookingSuccessfulData) => void
  prefillData?: {
    email?: string
    name?: string
    [key: string]: string | undefined
  }
  metadata?: Record<string, string>
  rescheduleUid?: string
  additionalContent?: string
  minDate?: string
  maxDate?: string
  routedTeamMemberIds?: number[]
  language?: string
  languageLocked?: boolean
  bookingFields?: BookingField[]
  lockAttendeeFields?: boolean
  routingFormResponses?: Record<string, string>
}

const CalendarEmbed: React.FC<CalendarEmbedProps> = ({
  calLink,
  title,
  description,
  height,
  onBookingSuccessful,
  prefillData,
  metadata,
  rescheduleUid,
  additionalContent,
  minDate,
  maxDate,
  routedTeamMemberIds,
  language,
  languageLocked,
  bookingFields,
  lockAttendeeFields,
  routingFormResponses,
}) => {
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const {t} = useTranslation()

  const {defaultLanguage} = useLanguageCriteria()

  const [calendarLoaded, setCalendarLoaded] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  // Add routing parameters to calLink if provided, and selected UI language param.
  const enhancedCalLink = React.useMemo(() => {
    const separator = calLink.includes('?') ? '&' : '?'

    const linkWithLanguage = `${calLink}${separator}uiLang=${defaultLanguage}`

    if (!routedTeamMemberIds || routedTeamMemberIds.length === 0) {
      return linkWithLanguage
    }
    return `${linkWithLanguage}&cal.routedTeamMemberIds=${routedTeamMemberIds.join(',')}`
  }, [calLink, routedTeamMemberIds, defaultLanguage])

  const namespace = enhancedCalLink.replace('/', '-')

  // Convert metadata to Cal.com format: metadata[key] = value
  // Include language in metadata so it's available in the booking webhook
  const metadataWithLanguage = {
    ...metadata,
    ...(language && {language}),
  }

  const calcomMetadata = metadataWithLanguage
    ? Object.entries(metadataWithLanguage).reduce(
        (acc, [key, value]) => {
          acc[`metadata[${key}]`] = value
          return acc
        },
        {} as Record<string, string>
      )
    : {}

  // Convert routing form responses to Cal.com format
  const calcomRoutingFormResponses = routingFormResponses
    ? Object.entries(routingFormResponses).reduce(
        (acc, [key, value]) => {
          acc[key] = value
          return acc
        },
        {} as Record<string, string>
      )
    : {}

  // Encode additional content HTML for Cal.com embed
  // Wrap in styled div to ensure proper rendering
  const encodedAdditionalContent = additionalContent
    ? encodeURIComponent(
        `<div style="margin: 8px 0; line-height: 1.6;">${additionalContent}</div>`
      )
    : undefined

  // Create a key that changes when title or description changes to force remount
  const embedKey = React.useMemo(
    () => `${namespace}-${title || ''}-${description || ''}-${language || ''}`,
    [namespace, title, description, language]
  )

  useEffect(() => {
    setCalendarLoaded(false)
  }, [namespace])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.originator === 'CAL' &&
        event.data?.type === 'calendarLoaded'
      ) {
        setCalendarLoaded(true)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  useEffect(() => {
    ;(async function () {
      try {
        const cal = await getCalApi({
          namespace,
          embedJsUrl: REACT_APP_CALCOM_EMBED_URL,
        })

        cal('ui', {
          hideEventTypeDetails: false,
          layout: 'month_view',
          theme: 'light',
          cssVarsPerTheme: {
            light: {
              'cal-border-booker-width': '0px',
              'cal-brand-color': colors.primary,
              'cal-brand-text': colors.primaryLight,
            },
            dark: {},
          },
          styles: {
            branding: {
              brandColor: colors.primary,
            },
          },
        })

        cal('on', {
          action: 'linkFailed',
          callback: (e: any) => {
            console.error('Cal.com embed link failed:', e) // eslint-disable-line no-console
            setCalendarLoaded(true)
            setSnackbarOpen(true)
          },
        })

        if (onBookingSuccessful) {
          // Listen for booking success (new bookings)
          cal('on', {
            action: 'bookingSuccessful',
            callback: (e: any) => {
              const {data} = e.detail
              onBookingSuccessful(data)
            },
          })

          // Listen for reschedule success
          cal('on', {
            action: 'rescheduleBookingSuccessful',
            callback: (e: any) => {
              const {data} = e.detail
              onBookingSuccessful(data)
            },
          })
        }
      } catch (error) {
        console.error('Cal.com embed initialization error:', error) // eslint-disable-line no-console
      }
    })()
  }, [namespace, onBookingSuccessful, colors])

  return (
    <Box>
      <AlertSnackBar
        open={snackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        type='error'
        message={t('calendar_embed_load_error')}
      />
      <Box position='relative' minHeight={700}>
        <Box
          width='100%'
          sx={
            height != null
              ? {
                  maxHeight: height,
                  overflow: 'auto',
                  opacity: calendarLoaded ? 1 : 0,
                  transition: 'opacity 500ms ease-in-out',
                }
              : {
                  overflow: 'visible',
                  opacity: calendarLoaded ? 1 : 0,
                  transition: 'opacity 500ms ease-in-out',
                }
          }
        >
          <Cal
            key={embedKey}
            namespace={namespace}
            calLink={enhancedCalLink}
            style={
              height != null
                ? {width: '100%', height: '100%', overflow: 'auto'}
                : {width: '100%', minHeight: 0}
            }
            config={{
              layout: 'month_view',
              ...(rescheduleUid && {rescheduleUid}),
              ...(title && {titleOverride: title}),
              ...(description && {descriptionOverride: description}),
              ...(encodedAdditionalContent && {
                additionalContent: encodedAdditionalContent,
              }),
              ...(minDate && {minDate}),
              ...(maxDate && {maxDate}),
              ...(language && {'cal.language': language}),
              ...(languageLocked !== undefined && {
                'cal.languageLocked': String(languageLocked),
              }),
              ...(lockAttendeeFields !== undefined && {
                'cal.lockAttendeeFields': String(lockAttendeeFields),
              }),
              ...prefillData,
              ...calcomMetadata,
              ...calcomRoutingFormResponses,
              ...(bookingFields && {
                'cal.bookingFields': JSON.stringify(bookingFields),
              }),
            }}
            calOrigin={REACT_APP_CALCOM_ORIGIN}
            embedJsUrl={REACT_APP_CALCOM_EMBED_URL}
          />
        </Box>
        <Box
          position='absolute'
          top={0}
          left={0}
          right={0}
          bottom={0}
          sx={{
            opacity: calendarLoaded ? 0 : 1,
            transition: 'opacity 500ms ease-in-out',
            pointerEvents: calendarLoaded ? 'none' : 'auto',
          }}
        >
          <CalendarLoader open={!calendarLoaded} productLine={productLine} />
        </Box>
      </Box>
    </Box>
  )
}

export default CalendarEmbed
